import type { ReactNode } from 'react';
import type { ThemeMode } from '../hooks/useTheme.ts';
import { AutoIcon, CrossMark, MoonIcon, NewIcon, SunIcon } from './icons.tsx';

const themeNames: Record<ThemeMode, string> = {
  auto: 'automático',
  light: 'claro',
  dark: 'oscuro',
};

/** Round 44 px buttons on the purple bar. */
export const headerButton =
  'grid h-11 w-11 place-items-center rounded-full text-cream/90 transition-colors ' +
  'hover:bg-white/10 hover:text-cream active:bg-white/15';

/**
 * The purple bar of every page: the cross, the name and a fine old-gold rule below.
 * Compact on purpose: on a phone in the street the screen is for the answers.
 */
export function HeaderShell({
  title,
  subtitle,
  titleAs: Title = 'h1',
  titleClass = 'text-[1.65rem]',
  children,
}: {
  title: string;
  subtitle: string;
  titleAs?: 'h1' | 'p';
  /** Font size of the title: smaller for long names */
  titleClass?: string;
  children?: ReactNode;
}) {
  return (
    <header className="border-b border-gold/70 bg-purple pt-[env(safe-area-inset-top)] text-cream dark:border-gold/40 dark:bg-night-800">
      <div className="mx-auto flex max-w-2xl items-center gap-2.5 px-4 py-1.5">
        <span className="text-gold">
          <CrossMark size={30} />
        </span>
        <div className="min-w-0 flex-1">
          <Title
            className={`truncate font-serif ${titleClass} leading-none font-semibold tracking-wide`}
          >
            {title}
          </Title>
          <p className="mt-1 truncate text-[0.7rem] leading-none font-medium tracking-[0.16em] text-gold uppercase">
            {subtitle}
          </p>
        </div>
        {children}
      </div>
    </header>
  );
}

export function Header({
  theme,
  onTheme,
  onNew,
  canReset,
}: {
  theme: ThemeMode;
  onTheme: () => void;
  onNew: () => void;
  canReset: boolean;
}) {
  return (
    <HeaderShell title="Cruz de IA" subtitle="Semana Santa de Sevilla">
      <a
        href="/"
        className="rounded-full px-3 py-2 text-sm font-medium text-cream/90 transition-colors hover:bg-white/10 hover:text-cream"
      >
        Inicio
      </a>
      {canReset && (
        <button
          type="button"
          className={headerButton}
          onClick={onNew}
          aria-label="Nueva conversación"
        >
          <NewIcon />
        </button>
      )}
      <ThemeButton theme={theme} onTheme={onTheme} />
    </HeaderShell>
  );
}

export function ThemeButton({ theme, onTheme }: { theme: ThemeMode; onTheme: () => void }) {
  return (
    <button
      type="button"
      className={headerButton}
      onClick={onTheme}
      aria-label={`Tema ${themeNames[theme]}. Cambiar tema`}
      title={`Tema ${themeNames[theme]}`}
    >
      {theme === 'auto' ? <AutoIcon /> : theme === 'light' ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}
